import { create } from "zustand";
import type { Unit } from "variables/enums";
import type { FetchError } from "utils/fetchData/types";
import type { ProductActions } from "./productActions";
import { createProductActions } from "./productActions";
import type {
  ProductInput,
  ProductOutput,
  ProductOutputStrict,
  AccessoryGroupId,
} from "./product.types";
import { WITHOUT_ACCESSORY_VALUE } from "variables";

export type ProductStore = {
  nodeId: string | null;
  input: ProductInput | null;
  output: ProductOutput | null;
  hasChanged: boolean;
  unit: Unit | null;
  isLoading: boolean;
  isSwitchingProduct: boolean;
  error: FetchError | Error | null;
  actions: ProductActions;
};

const useProductStore = create<ProductStore>()((set, get) => ({
  nodeId: null,
  input: null,
  output: null,
  hasChanged: false,
  unit: null,
  isLoading: false,
  isSwitchingProduct: false,
  error: null,
  actions: createProductActions(set, get),
}));

// Hooks
export const useProductInput = () => useProductStore((state) => state.input);
export const useProductOutput = () =>
  useProductStore((state) => state.output) as ProductOutputStrict | null;
export const useProductPimId = () => {
  const output = useProductStore((state) => state.output);
  return (
    output?.productImageList?.productImages?.find(
      (img) => img?.typ === "ProductPhoto",
    )?.pimId || null
  );
};
export const useProductName = () => {
  const output = useProductStore((state) => state.output);
  const urlParams = new URLSearchParams(window.location.search);
  const urlProductName = urlParams.get("productName");
  if (urlProductName) {
    return urlProductName;
  }
  return output?.product?.orderCode?.key || null;
};
export const useProductSeriesId = () => {
  const input = useProductStore((state) => state.input);
  return input?.seriesId || null;
};

export const useProductNodeId = () => useProductStore((state) => state.nodeId);

export const useHasProductChanged = () =>
  useProductStore((state) => state.hasChanged);
export const useProductUnit = () => useProductStore((state) => state.unit);
export const useIsProductLoading = () =>
  useProductStore((state) => state.isLoading);
export const useIsSwitchingProduct = () =>
  useProductStore((state) => state.isSwitchingProduct);
export const useProductError = () => useProductStore((state) => state.error);
export const useProductActions = () =>
  useProductStore((state) => state.actions);
export const useIsWizardProduct = () => {
  const input = useProductStore((state) => state.input);
  return !!input?.wizardType;
};

export const getProductActions = () => useProductStore.getState().actions;
export const getProductInput = () => useProductStore.getState().input;

// Utility functions
export const getProductName = () =>
  useProductStore.getState().output?.product?.orderCode?.key || null;

export const getAccessoryFromGroup = (
  presentation: string,
  accessoryGroupId: AccessoryGroupId,
) => {
  const output = useProductStore.getState().output;
  if (!output) {
    return null;
  }
  const accessoryGroups = output.product?.accessoryGroups;
  if (!accessoryGroups) {
    return null;
  }

  for (const group of accessoryGroups) {
    if (group?.id === accessoryGroupId) {
      const foundAccessory = group.accessories?.find(
        (acc) =>
          acc?.currentPresentation === presentation ||
          (presentation === WITHOUT_ACCESSORY_VALUE && acc?.seriesId === 0),
      );
      if (foundAccessory) {
        return foundAccessory;
      }
    }
  }
  return null;
};

export const getSelectedAccessory = (
  presentation: string,
  accessoryGroupId: AccessoryGroupId,
) => {
  const output = useProductStore.getState().output;
  if (!output) {
    return null;
  }
  const accessory = output.product?.selectedAccessories?.find(
    (acc) =>
      acc?.currentPresentation === presentation &&
      acc?.accessoryGroupId === accessoryGroupId,
  );
  if (!accessory) {
    return null;
  }

  const accessoryFromGroup = getAccessoryFromGroup(
    presentation,
    accessoryGroupId,
  );
  return {
    ...accessory,
    infoText: accessoryFromGroup?.infoText || null,
    serieAlias: accessoryFromGroup?.serieAlias || null,
    seriesName: accessoryFromGroup?.seriesName || null,
  };
};
